import { useEffect, useState } from 'react';
import { Alert, Stack, TableBody } from '@mui/material';
import { Badge, CustomButton, CustomInput, CustomSwitch, CustomTable, Text } from '../../shared/uiComponents';
import { CustomTableRow, RowTableCell } from '../../shared/uiComponents/Table/tableHelpers';
import { AdminHourServiceCode, AdminHours } from '../../redux/API/ClientAPIHelpers/adminHours';

import { errorNormalizer } from '../../shared/Helpers/functions';
import { EditSvg } from '../../shared/Assets/svgIcons';
import { PlusIcon } from '../../shared/Assets/svg';
import { TEXT } from '../../shared/Helpers/colors';
import { useScreenWidth } from '../../shared/Helpers/hooks';

const CodeForm = ({
	existing,
	onClose,
	onSave,
}: {
	existing: AdminHourServiceCode | null;
	onClose: () => void;
	onSave: () => void;
}) => {
	const [code, setCode] = useState(existing?.code || '');
	const [name, setName] = useState(existing?.name || '');
	const [active, setActive] = useState(existing?.isActive ?? true);
	const [saving, setSaving] = useState(false);

	const save = () => {
		setSaving(true);
		const data = { code: code.trim(), name: name.trim(), isActive: active };
		const action = AdminHours.saveServiceCode(data, existing?.id);
		action
			.then((response) => {
				if (response?.error) throw response.error;
				onSave();
				onClose();
			})
			.catch(errorNormalizer)
			.finally(() => setSaving(false));
	};

	return (
		<>
			<Text
				title={existing ? 'Edit service code' : 'Add service code'}
				fontWeight="semiBold"
				className="marginBottom16"
			/>
			<CustomInput
				label="Code"
				maxLength={50}
				value={code}
				onChange={(event) => setCode(event.target.value)}
				className="marginBottom16"
			/>
			<CustomInput
				label="Name"
				maxLength={200}
				value={name}
				onChange={(event) => setName(event.target.value)}
				className="marginBottom16"
			/>
			<CustomSwitch label="Active" checked={active} onChange={(event) => setActive(event.target.checked)} />
			<Stack direction="row" justifyContent="flex-end" alignItems="center" gap="12px" flexWrap="wrap" alignSelf="stretch" className="marginTop24">
				<CustomButton title="Cancel" buttonType="secondaryGray" onClick={onClose} disabled={saving} />
				<CustomButton
					title="Save service code"
					onClick={save}
					loading={saving}
					disabled={!code.trim() || !name.trim()}
				/>
			</Stack>
		</>
	);
};

const ServiceCodes = ({ onClose }: { onClose: () => void }) => {
	const compact = useScreenWidth() <= 500;
	const [codes, setCodes] = useState<AdminHourServiceCode[]>([]);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);
	const [open, setOpen] = useState(false);
	const [existing, setExisting] = useState<AdminHourServiceCode | null>(null);

	const load = () => {
		setLoading(true);
		setLoadError(false);

		return AdminHours.getServiceCodes(true)
			.then(setCodes)
			.catch((error) => {
				setLoadError(true);
				errorNormalizer(error);
			})
			.finally(() => setLoading(false));
	};
	useEffect(() => {
		load();
	}, []);

	if (open) {
		return <CodeForm existing={existing} onClose={() => setOpen(false)} onSave={load} />;
	}

	return (
		<>
			<Stack direction="row" alignItems="center" gap="12px" flexWrap="wrap" justifyContent="space-between" mb="20px">
				<Text title="Categories providers can select when logging Admin Hours." type="psm" color={TEXT.SECONDARY} />
				<CustomButton
					title="Add service code"
					startIcon={<PlusIcon />}
					onClick={() => {
						setExisting(null);
						setOpen(true);
					}}
				/>
			</Stack>
			{loadError ? (
				<Alert severity="error" action={<CustomButton title="Retry" buttonType="quaternary" onClick={load} />}>
					Service codes could not be loaded. Please try again.
				</Alert>
			) : (
				<CustomTable
					headers={[
						{ id: '1', name: compact ? 'Service code' : 'Code' },
						...(compact ? [] : [{ id: '2', name: 'Name' }]),
						{ id: '3', name: 'Status' },
						{ id: '4', name: 'Actions', width: compact ? '60px' : '80px' },
					]}
					data={{ query: codes.length ? codes : null }}
					loading={loading}
					hasPagination={false}
					hasMobile={false}
					TableBody={() => (
						<TableBody>
							{codes.map((item) => (
								<CustomTableRow key={item.id}>
									<RowTableCell sx={{ overflowWrap: 'anywhere' }}>
										{item.code}
										{compact && <Text title={item.name} type="pxs" color={TEXT.SECONDARY} />}
									</RowTableCell>
									{!compact && <RowTableCell>{item.name}</RowTableCell>}
									<RowTableCell>
										<Badge
											label={item.isActive ? 'Active' : 'Inactive'}
											type={item.isActive ? 'success' : 'gray'}
											sizeType="sm"
										/>
									</RowTableCell>
									<RowTableCell>
										<CustomButton
											aria-label={`Edit ${item.code}`}
											buttonType="quaternary"
											iconOnly={<EditSvg />}
											onClick={() => {
												setExisting(item);
												setOpen(true);
											}}
										/>
									</RowTableCell>
								</CustomTableRow>
							))}
						</TableBody>
					)}
				/>
			)}
			<Stack direction="row" justifyContent="flex-end" alignItems="center" gap="12px" flexWrap="wrap" alignSelf="stretch" className="marginTop24">
				<CustomButton title="Done" buttonType="secondaryGray" onClick={onClose} />
			</Stack>
		</>
	);
};

export default ServiceCodes;
